/* eslint-disable no-unused-vars */
// TODO: move nb.vo.TimelineData.xxx methods over here
// and leave nb.vo.TimelineData in vo/TimelineData.js
import { DashboardWidgetData } from 'features/widget/types'
import { TimelineSeries } from 'schemas/TimelineSeriesSchema'
import { TimelineAnnotationGroup } from 'schemas/TimelineAnnotationGroupSchema'
import { PivotResult } from 'schemas/PivotResultSchema'
import { TopicInfo } from 'schemas/TopicInfoSchema'
import { TimelineData } from 'schemas/TimelineDataSchema'
import { AnnotatedMetricResult } from 'schemas/AnnotatedMetricResultSchema'
import { SocialWebTimelineComparisonWidget } from 'schemas/SocialWebTimelineComparisonWidgetSchema'
import { AudienceTimelineComparisonWidget } from 'schemas/AudienceTimelineComparisonWidgetSchema'
import MetricTimeUnit, { MetricTimeUnitStrings } from 'enums/MetricTimeUnit'
import ComparisonMode from 'enums/ComparisonMode'
import ReturnStatus from 'enums/ReturnStatus'
import MetricCategoryItem, { MetricCategoryItemStrings } from 'enums/MetricCategoryItem'
import MetricSeries, { MetricSeriesStrings } from 'enums/MetricSeries'
import ViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import { TimeZoneStrings } from 'enums/TimeZone'

import { getParentTypes } from './MetricItemUtil'
import { getIntervalObj, addUserTzInterval, IntervalObj } from './dateUtil'
import { TimelineSeriesVo, TimelineDataVo } from '../vo/TimelineData'
import * as measureTypeUtil from './measureTypeUtil'
import * as metricCategoryItemUtil from './metricCategoryItemUtil'

export function groupTimelineSeries(seriesSet: TimelineSeries[]) {
  const seriesMap = new Map()
  for (let i = 0; i < seriesSet.length; i += 1) {
    const key = seriesSet[i].type
    if (!seriesMap.get(key)) {
      seriesMap.set(key, new Map())
    }
    const categoryMap = seriesMap.get(key)
    categoryMap.set(seriesSet[i].category, seriesSet[i])
  }
  return seriesMap
}

export function createColumns(
  min: number,
  max: number,
  timeUnit: string,
  timeZone?: TimeZoneStrings
) {
  if (!min || !max) {
    return []
  }
  let interval: IntervalObj
  let daylightSavingOffset = 0

  // assume daylight saving time won't be more than half day.
  const halfDay = 43200000
  switch (timeUnit) {
    case MetricTimeUnit.MONTH:
      interval = getIntervalObj(0, 1)
      daylightSavingOffset = halfDay
      break
    case MetricTimeUnit.WEEK:
      interval = getIntervalObj(0, 0, 7)
      daylightSavingOffset = halfDay
      break
    case MetricTimeUnit.DAY:
      interval = getIntervalObj(0, 0, 1)
      daylightSavingOffset = halfDay
      break
    case MetricTimeUnit.HOUR:
      interval = getIntervalObj(0, 0, 0, 1)
      break
    case MetricTimeUnit.QUARTER_HOUR:
      interval = getIntervalObj(0, 0, 0, 0, 15)
      break
    case MetricTimeUnit.MINUTE:
      interval = getIntervalObj(0, 0, 0, 0, 1)
      break
    default:
      break
  }

  const minDate = new Date()
  minDate.setTime(min)

  const columns = []
  let column = minDate

  while (column.getTime() <= max + daylightSavingOffset) {
    columns.push(new Date(column.getTime()))
    switch (timeUnit) {
      case MetricTimeUnit.MONTH:
      case MetricTimeUnit.WEEK:
      case MetricTimeUnit.DAY:
        column = addUserTzInterval(column, interval!, timeZone)
        break
      case MetricTimeUnit.HOUR:
      case MetricTimeUnit.QUARTER_HOUR:
      case MetricTimeUnit.MINUTE:
        if (interval!.hours) {
          column.setUTCHours(column.getUTCHours() + interval!.hours)
        }
        if (interval!.minutes) {
          column.setUTCMinutes(column.getUTCMinutes() + interval!.minutes)
        }
        if (interval!.seconds) {
          column.setUTCSeconds(column.getUTCSeconds() + interval!.seconds)
        }
        break
      default:
        break
    }

    if (column.getTime() === columns[columns.length - 1].getTime()) {
      break
    }
  }
  return columns
}

export function fillInZero(fullRange: Date[], startDT: number, endDT: number, valueSet: number[]) {
  const result: number[] = []
  if (!valueSet) {
    return result
  }
  if (fullRange.length === valueSet.length) {
    return [...valueSet]
  }

  // CB-14924, server java's daylight saving info doesn't match to client side
  // javascript's daylight saving info. Assume javascript's daylight saving info
  // is more correct, since it is updated more often. Workaround: find the
  // nearest matches of the date.
  let startIndex
  let endIndex
  let startDiff = Number.MAX_VALUE
  let endDiff = Number.MAX_VALUE
  let i
  let t
  for (i = 0; i < fullRange.length; i += 1) {
    t = fullRange[i].getTime()
    if (Math.abs(startDT - t) < startDiff) {
      startDiff = Math.abs(startDT - t)
      startIndex = i
    }

    if (Math.abs(endDT - t) < endDiff) {
      endDiff = Math.abs(endDT - t)
      endIndex = i
    }

    result.push(0)
  }

  if (startIndex === undefined || endIndex === undefined || startIndex > endIndex) {
    throw new Error('cannot adjust value set')
  }

  for (i = startIndex; i <= endIndex; i += 1) {
    result[i] = valueSet[i - startIndex] ? valueSet[i - startIndex] : 0
  }
  return result
}

export function fromSearchCrosstabResult(
  results: DashboardWidgetData<PivotResult>[],
  setting: SocialWebTimelineComparisonWidget | AudienceTimelineComparisonWidget,
  seriesTypes?: MetricSeriesStrings[],
  timeZone?: TimeZoneStrings
) {
  let { crossTabs } = results![0].results![0].data
  let metricSet = crossTabs![0].metrics
  // CB-15543
  // Set default values in case selectedTimeUnit does not match time units
  let timeUnitIndex = 0
  let { timeUnit } = metricSet![0]

  for (let i = 0; i < metricSet!.length; i += 1) {
    if (metricSet![i].timeUnit === setting.timeUnit) {
      timeUnitIndex = i
      timeUnit = setting.timeUnit
      break
    }
  }
  // Find min start and max end of the topic
  // give a default value which start is now and end is zero
  let fullDateRangeStart = new Date().getTime()
  let fullDateRangeEnd = 0
  for (let i = 0; i < results.length; i += 1) {
    metricSet = results![i].results![0].data.crossTabs![0].metrics
    const start = metricSet![timeUnitIndex].columns![0]!
    const end = metricSet![timeUnitIndex].columns![metricSet![timeUnitIndex].columns!.length - 1]!
    if (fullDateRangeStart > start) {
      fullDateRangeStart = start
    }
    if (fullDateRangeEnd < end) {
      fullDateRangeEnd = end
    }
  }
  const pivots: { [key: number]: string } = {}
  const themeInfos = setting.pivotThemeInfos
  // sorting themes
  for (let i = 0; i < themeInfos!.length; i += 1) {
    pivots[themeInfos![i].themeId!] = themeInfos![i].name!
  }

  // Create full range date time
  const fullColumns = createColumns(fullDateRangeStart, fullDateRangeEnd, timeUnit!, timeZone)

  // Generate each series
  let series
  const seriesSet = []
  let dataSet
  let metric
  for (let i = 0; i < results.length; i += 1) {
    crossTabs = results![i].results![0].data.crossTabs

    for (let j = 0; j < crossTabs!.length; j += 1) {
      metric = crossTabs![j].metrics![timeUnitIndex]

      for (let k = 0; k < metric.dataset!.length; k += 1) {
        if (!(seriesTypes && !(seriesTypes as string[]).includes(metric.dataset![k].seriesName!))) {
          series = TimelineSeriesVo()
          series.topicId = setting.selectedTopic.topicId!
          switch (setting.comparisonMode) {
            case ComparisonMode.TAGS:
              series.topicName = crossTabs![j].tagName!
              break
            case ComparisonMode.DOC_HIERARCHIES:
              series.topicName = crossTabs![j].docName!
              break
            case ComparisonMode.THEMES:
              series.topicName = pivots[crossTabs![j].themeId!]
              break
            default:
          }
          dataSet = metric.dataset![k]
          series.data = fillInZero(
            fullColumns,
            metric.columns![0]!,
            metric.columns![metric.columns!.length - 1]!,
            dataSet.set as number[]
          )
          series.isSupported = !(dataSet.status === ReturnStatus.LANGUAGE_NOT_SUPPORTED)
          series.isNoCount = dataSet.status === ReturnStatus.NO_COUNT
          series.type = dataSet.seriesName
          series.isNegative = false
          switch (series.type) {
            case MetricSeries.NEGATIVE_SENTIMENT:
              series.isNegative = true
              break
            default:
              series.isNegative = false
              break
          }
          seriesSet.push(series)
        }
      }
    }
  }

  const timelineData = TimelineDataVo()
  timelineData.columns = (fullColumns as unknown) as number[]
  timelineData.series = seriesSet

  return timelineData
}

export function parseNotes(results: DashboardWidgetData<AnnotatedMetricResult>[]) {
  let ret: TimelineAnnotationGroup[] = []
  for (let i = 0; i < results.length; i += 1) {
    ret = ret.concat(results![i].results![0].data!.timelineNoteGroups!)
  }
  return ret
}
export function countAverage(
  categoryMap: Map<MetricCategoryItemStrings, TimelineSeries>,
  categories: MetricCategoryItemStrings[]
) {
  if (!categoryMap || !categories) {
    return
  }

  let sum: number[] | undefined
  let count: number[] | undefined
  const keys = Array.from(categoryMap.keys())
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]
    if (categories.includes(key)) {
      const set = categoryMap.get(keys[i])!.data
      count = count ? count.map((num, idx) => num + set![idx]!) : (set as number[])

      const values = categoryMap
        .get(keys[i])!
        .data!.map((x) => x! * metricCategoryItemUtil.getValue(key))
      sum = sum ? sum.map((x, idx) => x + values[idx]) : values
    }
  }
  const categoryNone = categoryMap.get(MetricCategoryItem.NONE)
  if (!!sum && !!categoryNone) {
    categoryNone.data = sum.map((x, idx) => {
      if (count![idx] > 0) return x / count![idx]
      return x
    })
  }
}

export function parseStarRating(categoryMap: Map<MetricCategoryItemStrings, TimelineSeries>) {
  countAverage(categoryMap, metricCategoryItemUtil.STAR_RATING_COUNT_OPTIONS)
}

export function groupScore(
  categoryMap: Map<MetricCategoryItemStrings, TimelineSeries>,
  categories: MetricCategoryItemStrings[],
  targetCategory: MetricCategoryItemStrings
) {
  if (!categoryMap || !categories) {
    return
  }

  let count
  let replaceMetric = null
  for (let i = 0; i < categories.length; i += 1) {
    const metricSeries = categoryMap.get(categories[i])
    if (metricSeries) {
      if (!replaceMetric) {
        replaceMetric = metricSeries
      }
      count = count ? count.map((num, idx) => num! + metricSeries.data![idx]!) : metricSeries.data
    }
  }
  if (!!replaceMetric && !!count) {
    replaceMetric.category = targetCategory
    replaceMetric.data = count
  }
}

export function parseSurveyScore(categoryMap: Map<MetricCategoryItemStrings, TimelineSeries>) {
  countAverage(categoryMap, metricCategoryItemUtil.SURVEY_SCORE_COUNT_OPTIONS)
  groupScore(categoryMap, [MetricCategoryItem.TEN_SCORE], MetricCategoryItem.TEN_SCORE)
  groupScore(
    categoryMap,
    [MetricCategoryItem.NINE_SCORE, MetricCategoryItem.EIGHT_SCORE, MetricCategoryItem.SEVEN_SCORE],
    MetricCategoryItem.HIGH_SCORE
  )
  groupScore(
    categoryMap,
    [MetricCategoryItem.SIX_SCORE, MetricCategoryItem.FIVE_SCORE, MetricCategoryItem.FOUR_SCORE],
    MetricCategoryItem.MEDIUM_SCORE
  )
  groupScore(
    categoryMap,
    [MetricCategoryItem.THREE_SCORE, MetricCategoryItem.TWO_SCORE, MetricCategoryItem.ONE_SCORE],
    MetricCategoryItem.LOW_SCORE
  )
  groupScore(categoryMap, [MetricCategoryItem.NO_SCORE], MetricCategoryItem.NO_SCORE)
  groupScore(
    categoryMap,
    [MetricCategoryItem.NO_RESPONSE_SURVEY],
    MetricCategoryItem.NO_RESPONSE_SURVEY
  )
}

export function parseNetPromoterScore(categoryMap: Map<MetricCategoryItemStrings, TimelineSeries>) {
  if (!categoryMap) {
    return
  }

  let promoter: number[] | undefined
  let demoter: number[] | undefined
  let passive: number[] | undefined
  if (categoryMap.has(MetricCategoryItem.PROMOTER)) {
    promoter = categoryMap.get(MetricCategoryItem.PROMOTER)!.data as number[]
  }
  if (categoryMap.has(MetricCategoryItem.DETRACTOR)) {
    demoter = categoryMap.get(MetricCategoryItem.DETRACTOR)!.data as number[]
  }
  if (categoryMap.has(MetricCategoryItem.PASSIVE)) {
    passive = categoryMap.get(MetricCategoryItem.PASSIVE)!.data as number[]
  }

  const categoryNone = categoryMap.get(MetricCategoryItem.NONE)
  if (!!promoter && !!demoter && !!passive && !!categoryNone) {
    categoryNone.data = promoter.map((x, idx) => {
      const count = x + passive![idx] + demoter![idx]
      if (count > 0) {
        return ((x - demoter![idx]) / count) * 100
      }
      return 0
    })
  }
}

export function fromSearchResult(
  results: DashboardWidgetData<AnnotatedMetricResult>[],
  topicInfos: TopicInfo[],
  selectedTimeUnit: MetricTimeUnitStrings,
  seriesTypes?: string[],
  viewOption?: InsightViewOptionStrings | null,
  timeZone?: TimeZoneStrings
) {
  let metricSet = results![0].results![0].data.metrics
  // CB-15543
  // Set default values in case selectedTimeUnit does not match time units
  let timeUnitIndex = 0
  let { timeUnit } = metricSet![0]

  for (let i = 0; i < metricSet!.length; i += 1) {
    if (metricSet![i].timeUnit === selectedTimeUnit) {
      timeUnitIndex = i
      timeUnit = selectedTimeUnit!
      break
    }
  }
  // Find min start and max end of all of topics
  // give a default value which start is now and end is zero
  let fullDateRangeStart = new Date().getTime()
  let fullDateRangeEnd = 0
  for (let i = 0; i < results.length; i += 1) {
    metricSet = results![i].results![0].data.metrics
    const start = metricSet![timeUnitIndex].columns![0]!
    const end = metricSet![timeUnitIndex].columns![metricSet![timeUnitIndex].columns!.length - 1]!

    if (fullDateRangeStart > start) {
      fullDateRangeStart = start
    }
    if (fullDateRangeEnd < end) {
      fullDateRangeEnd = end
    }
  }

  // Create full range date time
  const fullColumns = createColumns(fullDateRangeStart, fullDateRangeEnd, timeUnit!, timeZone)

  // Generate each series
  let series: TimelineSeries
  const seriesSet: TimelineSeries[] = []
  let dataSet
  let metric
  const seriesNames: string[] = []
  let topicIdx
  const ratio = results.length === topicInfos.length ? 1 : 2
  for (let i = 0; i < results.length; i += 1) {
    // Assume results is a ordered collection.
    // The time unit of each topic are in the same index.
    metric = results![i].results![0].data.metrics![timeUnitIndex]
    topicIdx = topicInfos.length === 1 ? 0 : Math.floor(i / ratio)
    for (let j = 0; j < metric.dataset!.length; j += 1) {
      if (!(seriesTypes && !seriesTypes.includes(metric.dataset![j].seriesName!))) {
        series = TimelineSeriesVo()
        series.topicId = topicInfos[topicIdx].topicId!
        series.topicName = topicInfos[topicIdx].name!

        dataSet = metric.dataset![j]
        series.data = fillInZero(
          fullColumns,
          metric.columns![0]!,
          metric.columns![metric.columns!.length - 1]!,
          dataSet.set as number[]
        )
        series.isSupported = !(dataSet.status === ReturnStatus.LANGUAGE_NOT_SUPPORTED)
        series.isNoCount = dataSet.status === ReturnStatus.NO_COUNT
        series.type = dataSet.seriesName
        series.isNegative = false
        series.category = dataSet.categoryItem || MetricCategoryItem.NONE
        series.isChild = !!dataSet.categoryItem && dataSet.categoryItem !== MetricCategoryItem.NONE
        if (!seriesNames.includes(series.type!)) {
          seriesNames.push(series.type!)
        }
        if (viewOption === ViewOption.OVERVIEW) {
          if (
            !([
              MetricSeries.TOTAL_BUZZ,
              MetricSeries.UNIQUE_AUTHORS,
              MetricSeries.OVERALL_STAR_RATINGS,
              MetricSeries.OVERALL_SURVEY_SCORES,
              MetricSeries.NET_PROMOTER_SCORES
            ] as string[]).includes(series.type!)
          ) {
            const parents = getParentTypes(viewOption)
            let isParent = false
            for (let k = 0; k < parents.length; k += 1) {
              if (
                (measureTypeUtil.getMetricSeries(parents[k]) as string[]).includes(series.type!)
              ) {
                isParent = true
                break
              }
            }
            series.isChild = !isParent
          }
        }
        switch (series.type) {
          case MetricSeries.NEGATIVE_SENTIMENT:
            series.isNegative = true
            break
          default:
            series.isNegative = false
            break
        }
        seriesSet.push(series)
      }
    }
  }

  // CB-23487, New Summary for VoC |
  // New chart for star rating, survey score, NPS
  const killChild = seriesNames.length > 1
  const seriesMap = groupTimelineSeries(seriesSet)
  switch (viewOption) {
    case ViewOption.STAR_RATINGS:
      for (let i = 0; i < seriesNames.length; i += 1) {
        parseStarRating(seriesMap.get(seriesNames[i]))
      }
      break
    case ViewOption.SURVEY_SCORES:
      for (let i = 0; i < seriesNames.length; i += 1) {
        parseSurveyScore(seriesMap.get(seriesNames[i]))
      }
      break
    default:
      parseStarRating(seriesMap.get(MetricSeries.OVERALL_STAR_RATINGS))
      parseSurveyScore(seriesMap.get(MetricSeries.OVERALL_SURVEY_SCORES))
      parseNetPromoterScore(seriesMap.get(MetricSeries.NET_PROMOTER_SCORES))
      break
  }

  for (let i = seriesSet.length - 1; i >= 0; i -= 1) {
    switch (viewOption) {
      case ViewOption.STAR_RATINGS:
      case ViewOption.SURVEY_SCORES:
        if (killChild) {
          if (seriesSet[i].category !== MetricCategoryItem.NONE) {
            seriesSet.splice(i, 1)
            // eslint-disable-next-line no-continue
            continue
          }
        }
        break
      default:
        if (
          seriesSet[i].type === MetricSeries.OVERALL_STAR_RATINGS ||
          seriesSet[i].type === MetricSeries.OVERALL_SURVEY_SCORES ||
          seriesSet[i].type === MetricSeries.NET_PROMOTER_SCORES
        ) {
          if (killChild) {
            if (seriesSet[i].category !== MetricCategoryItem.NONE) {
              seriesSet.splice(i, 1)
              // eslint-disable-next-line no-continue
              continue
            }
          }
        }
    }
    if (
      (metricCategoryItemUtil.SURVEY_SCORE_COUNT_OPTIONS as string[]).includes(
        seriesSet![i].category!
      ) &&
      !(metricCategoryItemUtil.SURVEY_SCORE_GROUP_OPTIONS as string[]).includes(
        seriesSet![i].category!
      )
    ) {
      seriesSet.splice(i, 1)
    }
  }

  const timelineData: TimelineData = TimelineDataVo()
  timelineData.columns = (fullColumns as unknown) as number[]
  timelineData.series = seriesSet
  timelineData.noteGroups = parseNotes(results)

  return timelineData
}
